import { useCallback, useState } from 'react';
import type { Column } from '@tanstack/react-table';
import { DataTableTypes } from '../../DataTableTypes';
import { useDebounce } from '../../../../utils/useDebounce';
import { internalColumnType, internalColumnTypes } from '../const/internalColumnType';
import { useDataTableContext } from '../context/DataTableContext';

export function useColumnConfiguration() {
  const { tableInstance, actions } = useDataTableContext();
  const tableState = tableInstance.getState();
  const columnOrder = tableState.columnOrder;
  const columnSizing = tableState.columnSizing;

  // noinspection PointlessBooleanExpressionJS
  const hiddenColumns = Object.entries(tableState.columnVisibility)
    .filter(([_, value]) => value === false)
    .map(([key]) => key);
  const allColumns = tableInstance.getAllLeafColumns();

  return {
    managedColumns: allColumns.filter((it) => !internalColumnTypes.includes(it.id)),
    state: {
      allVisible: hiddenColumns.length === 0
    },
    model: {
      columnOrder,
      columnSizing,
      hiddenColumns
    },
    setColumnOrder(nextColumnOrder: string[]) {
      const columnOrder = [
        internalColumnType.selectRow,
        internalColumnType.expandRow,
        ...nextColumnOrder,
        internalColumnType.menu
      ];
      actions.setColumnConfigurationChange?.({
        columnOrder
      });
      tableInstance.setColumnOrder(columnOrder);
    },
    setHiddenColumn(column: Column<any, any>) {
      const nextIsVisible = !column.getIsVisible();
      actions.setColumnConfigurationChange?.({
        hiddenColumns: nextIsVisible ? hiddenColumns.filter((it) => it !== column.id) : [...hiddenColumns, column.id]
      });
      column.toggleVisibility(nextIsVisible);
    }
  };
}

export function useColumnConfigurationStore(
  initialState: DataTableTypes.ColumnConfiguration<any> | undefined,
  callback?: (state: DataTableTypes.ColumnConfiguration<any>) => void
) {
  const [state, setState] = useState<DataTableTypes.ColumnConfiguration<any>>({
    columnSizing: {},
    hiddenColumns: [],
    columnOrder: [],
    ...initialState
  });
  const debouncedCallback = useDebounce(callback ?? noop, 300);

  return {
    columnConfiguration: state,
    setColumnConfiguration: useCallback(
      (changedState: DataTableTypes.ColumnConfiguration<any>, debounce: boolean = true) => {
        const nextState = {
          ...state,
          ...changedState
        };

        setState(nextState);
        if (!!callback) {
          debounce ? debouncedCallback(nextState) : callback(nextState);
        }
      },
      [state, callback, debouncedCallback]
    )
  };
}

function noop() {}
